/**
 * Cold-deck rehydrate completion tracking.
 *
 * A small module-scoped resolver map that pairs a `request-deck-cards`
 * send with the eventual `deck-cards-loaded` server reply, so callers
 * can `await` full rehydration before reading the cards out of the
 * store (see `sharing.share` for the canonical use case).
 *
 * The pairing key is `deckId`. If two callers request the same deck
 * concurrently, both await the same resolver entry — the server's
 * single completion message resolves all waiters.
 */

type Waiter = {
  resolve: () => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
};

const waiters = new Map<string, Waiter[]>();

/**
 * Called by the adapter when a `deck-cards-loaded` server message
 * arrives. Resolves every waiter registered against this deckId.
 */
export function notifyDeckRehydrated(deckId: string): void {
  const list = waiters.get(deckId);
  if (!list) return;
  waiters.delete(deckId);
  for (const w of list) {
    clearTimeout(w.timer);
    w.resolve();
  }
}

/**
 * Returns a promise that resolves on the next `deck-cards-loaded`
 * arrival for `deckId`, or rejects after `timeoutMs`. The caller must
 * trigger the `request-deck-cards` send itself; this only tracks the
 * reply.
 */
export function awaitDeckRehydrated(deckId: string, timeoutMs: number): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      const list = waiters.get(deckId);
      if (!list) return;
      const next = list.filter((w) => w.timer !== timer);
      if (next.length === 0) waiters.delete(deckId);
      else waiters.set(deckId, next);
      reject(new Error(`Cold-deck rehydrate timed out for ${deckId}`));
    }, timeoutMs);
    const list = waiters.get(deckId) ?? [];
    list.push({ resolve, reject, timer });
    waiters.set(deckId, list);
  });
}
